<template>
  <section class="flow-selector" aria-labelledby="flow-heading">
    <header class="head">
      <h2 id="flow-heading" class="flow-title">¿Cómo quieres empezar?</h2>
      <p class="flow-intro">
        Elige una opción y te mostramos solo lo que hace falta llenar.
      </p>
    </header>

    <div class="flow-options" role="group" aria-labelledby="flow-heading">
      <button
        v-for="option in visibleOptions"
        :key="option.id"
        type="button"
        class="flow-card"
        :class="{ 'is-selected': modelValue === option.id }"
        :aria-pressed="modelValue === option.id"
        @click="$emit('update:modelValue', option.id)"
      >
        <component :is="option.icon" :size="36" :stroke-width="1.7" class="flow-icon" />
        <span class="flow-name">{{ option.name }}</span>
        <span class="flow-desc">{{ option.description }}</span>
        <span class="flow-action">
          {{ option.action }}
          <ArrowRight :size="18" :stroke-width="3" />
        </span>
      </button>
    </div>

    <!-- Un único control bajo el grupo de tarjetas (plan 25_08_2026, R3):
         cierra el módulo abierto y devuelve la página al estado de recarga,
         con el catálogo a la vista. -->
    <div v-if="modelValue" class="flow-close">
      <button type="button" class="btn btn--secondary" @click="$emit('update:modelValue', null)">
        <X :size="18" :stroke-width="2.5" />
        Cerrar y volver al catálogo
      </button>
    </div>
  </section>
</template>

<script>
import { ArrowRight, UploadCloud, PencilRuler, Puzzle, X } from 'lucide-vue-next';

export default {
  name: 'FlowSelector',
  components: { ArrowRight, UploadCloud, PencilRuler, Puzzle, X },
  props: {
    // null | 'print' | 'modeling' | 'custom'
    modelValue: { type: String, default: null },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      options: [
        {
          id: 'print',
          name: 'Ya tengo mi archivo 3D',
          description: 'Súbelo en .stl, .ply u .obj y te cotizamos la impresión.',
          action: 'Cargar archivo 3D',
          icon: 'UploadCloud',
        },
        {
          id: 'modeling',
          name: 'Necesito que diseñen mi pieza',
          description: 'Cuéntanos qué necesitas y adjunta una referencia: nosotros modelamos e imprimimos.',
          action: 'Enviar referencia',
          icon: 'PencilRuler',
        },
        {
          id: 'custom',
          name: 'Quiero personalizar un producto',
          description: 'Sube tu foto y la convertimos en un rompecabezas o un estuche a tu medida.',
          action: 'Personalizar con mi foto',
          icon: 'Puzzle',
          // Depende del procesamiento de imágenes en servidor (fase F3).
          feature: 'customizer',
        },
      ],
    };
  },
  computed: {
    visibleOptions() {
      const customizerEnabled = process.env.VUE_APP_FEATURE_CUSTOMIZER === 'true';
      return this.options.filter(
        (option) => option.feature !== 'customizer' || customizerEnabled
      );
    },
  },
};
</script>

<style scoped>
.head {
  text-align: center;
  margin-bottom: var(--space-md);
}

.flow-title {
  font-family: var(--font-subtitle);
  font-weight: 700;
  font-size: 32px;
  line-height: 1.15;
  color: var(--color-title);
  margin: 0 0 var(--space-xs) 0;
}

.flow-intro {
  font-family: var(--font-body);
  font-size: 18px;
  line-height: 1.5;
  color: var(--color-text);
  margin: 0;
}

.flow-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
  gap: var(--space-md);
}

.flow-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  text-align: left;
  padding: var(--space-md);
  background-color: var(--color-white);
  border: 2px solid var(--color-border);
  border-radius: 16px;
  cursor: pointer;
  font: inherit;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.flow-card:hover {
  transform: translateY(-3px);
  border-color: var(--color-action);
  box-shadow: 0px 14px 30px -12px rgba(82, 109, 130, 0.45);
}

.flow-card:focus-visible {
  outline: 3px solid var(--color-action);
  outline-offset: 2px;
}

.flow-card.is-selected {
  border-color: var(--color-brand);
  background-color: rgba(46, 138, 153, 0.06);
}

.flow-icon {
  color: var(--color-brand);
}

.flow-name {
  font-family: var(--font-subtitle);
  font-weight: 700;
  font-size: 21px;
  line-height: 1.2;
  color: var(--color-title);
}

.flow-desc {
  font-family: var(--font-body);
  font-size: 17px;
  line-height: 1.4;
  color: var(--color-text);
}

.flow-action {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: var(--space-xs);
  font-family: var(--font-body);
  font-size: 17px;
  color: var(--color-action);
}

.flow-card.is-selected .flow-action {
  color: var(--color-brand);
}

.flow-close {
  display: flex;
  justify-content: center;
  margin-top: var(--space-md);
}

@media (max-width: 800px) {
  .flow-options { grid-template-columns: 1fr; }
  .flow-title { font-size: 1.6rem; text-align: center; margin-bottom: var(--space-sm); }
}
</style>
